// What a rest-day choice actually means, said out loud before it is saved.
//
// A tenant's attendance policy was once saved with Mon–Fri marked as REST
// days — almost certainly someone reading the control as "the days we work".
// The form accepted it in silence, and every coverage figure downstream
// quietly became nonsense: September went from 25 working days to 7 and
// reported 20.7% coverage, with nothing anywhere saying why.
//
// The control is correctly labelled. That was not enough. A setting whose
// consequence is invisible at the moment of saving needs the consequence
// shown, not a better noun.

export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Working-day indices (Monday = 0), ascending. */
export const workingDayIndexes = (restDays: number[]): number[] =>
  [0, 1, 2, 3, 4, 5, 6].filter((d) => !(restDays || []).includes(d));

/** "Mon–Fri", "Mon–Wed, Fri", "Sat, Sun" — contiguous runs collapse. */
export const describeWorkingWeek = (restDays: number[]): string => {
  const days = workingDayIndexes(restDays);
  if (days.length === 0) return '';
  const runs: number[][] = [];
  for (const d of days) {
    const last = runs[runs.length - 1];
    if (last && d === last[last.length - 1] + 1) last.push(d);
    else runs.push([d]);
  }
  return runs
    .map((r) => (r.length >= 3 ? `${DAY_NAMES[r[0]]}–${DAY_NAMES[r[r.length - 1]]}` : r.map((d) => DAY_NAMES[d]).join(', ')))
    .join(', ');
};

/** The warning for a choice that is probably an inversion, or null.
 *
 *  It warns, never blocks: a two-day working week is unusual, not invalid,
 *  and a weekend-shift team is a real thing. Refusing to save would be us
 *  deciding we know the tenant's business better than they do. */
export const restDayWarning = (restDays: number[]): string | null => {
  const working = workingDayIndexes(restDays);
  if (working.length === 0) {
    return 'Every day is marked as a rest day, so no day will ever count as worked. Attendance coverage and overtime will both read as zero.';
  }
  if (working.length <= 2) {
    return `Only ${describeWorkingWeek(restDays)} would count as working days. `
      + 'If you meant to pick the days people DO work, pick the opposite — these are the days they do not.';
  }
  return null;
};
